import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Shared dropdown mechanics: open/close, debounced search, click-outside, "Load more" paging.
 *
 * `fetchFn`'s optional second `page` argument is additive — a 1-arg `(search?) => Promise<T[]>`
 * (every pre-existing caller) is still structurally assignable here, so this is backward
 * compatible; callers that never pass a `page` value simply never see more than `fetchFn`'s own
 * first page. `hasMore` is inferred, not counted against a known page size (this hook has no
 * opinion on one): it starts true and flips false the first time a page (initial or "load more")
 * comes back empty — real, reported gap (Binoy, 2026-09-13, "there are no pagination next as
 * well" on FormLookupField): the backend already supports `PageInfo.PageNumber/PageSize` paging,
 * this hook just never asked for a second page.
 */
export function useLookupDropdown<T>(
  fetchFn: (search?: string, page?: number) => Promise<T[]>,
  debounceMs = 250,
) {
  const [open, setOpen]         = useState(false);
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState<T[]>([]);
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]   = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic counter: lets in-flight fetches detect they've been superseded
  const reqIdRef = useRef(0);
  // Current page number for the active query — reset to 1 whenever a fresh search fires.
  const pageRef = useRef(1);
  // Ref copy of open so toggleDropdown doesn't need open in its dep array
  const openRef = useRef(false);
  openRef.current = open;

  // Cleanup debounce on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Clear stale results whenever the data source changes (e.g. formID switch)
  useEffect(() => { setResults([]); }, [fetchFn]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const search = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      pageRef.current = 1;
      setLoading(true);
      try {
        const items = await fetchFn(q || undefined, 1);
        if (reqId !== reqIdRef.current) return; // superseded by a newer request
        const list = Array.isArray(items) ? items : [];
        setResults(list);
        setHasMore(list.length > 0);
      } catch {
        if (reqId !== reqIdRef.current) return;
        setResults([]);
        setHasMore(false);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, debounceMs);
  }, [fetchFn, debounceMs]);

  // "Load more": appends the next page to the existing results for the CURRENT query. No-ops
  // while a search/another loadMore is already in flight, or once a page has come back empty.
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const reqId = ++reqIdRef.current;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    try {
      const items = await fetchFn(query || undefined, nextPage);
      if (reqId !== reqIdRef.current) return; // superseded by a newer search/loadMore
      const list = Array.isArray(items) ? items : [];
      if (list.length > 0) {
        pageRef.current = nextPage;
        setResults(prev => [...prev, ...list]);
      }
      setHasMore(list.length > 0);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setHasMore(false);
    } finally {
      if (reqId === reqIdRef.current) setLoadingMore(false);
    }
  }, [fetchFn, query, loading, loadingMore, hasMore]);

  // Toggle: clicking the trigger while open closes the dropdown
  const toggleDropdown = useCallback(() => {
    if (openRef.current) { setOpen(false); return; }
    setOpen(true);
    setQuery('');
    search('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [search]);

  const closeDropdown = useCallback(() => setOpen(false), []);

  const handleQueryChange = useCallback((q: string) => {
    setQuery(q);
    search(q);
  }, [search]);

  return {
    open, query, results, loading,
    loadingMore, hasMore, loadMore,
    containerRef, inputRef,
    toggleDropdown, closeDropdown, handleQueryChange, search,
  };
}
